/**
 * Disclosure Send Service
 *
 * Frontend service for sending disclosures via the Encompass Docs API.
 * Communicates with the send-disclosures Edge Function.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('DisclosureSendService', 'disclosure-dashboard');

// ============================================
// Types
// ============================================

export interface PlanCodeInfo {
  id: string;
  description: string | null;
  type: string | null;
}

export interface PlanCode {
  code: string;
  description: string;
  investorName?: string;
  investorCode?: string;
  loanType?: string;
  loanProgramtype?: string;
}

export interface ApplyPlanCodeResult {
  success: boolean;
  status: 'SUCCESS' | 'CONFLICT' | 'SUCCESS_WITH_CONFLICTS';
  conflicts: Array<{
    description: string;
    encompassFieldId: string;
    encompassValue: string;
    planValue: string;
  }>;
  /** Error message from Encompass (e.g., "Loan is locked by another user") */
  error?: string;
}

export interface DisclosureLoanInfo {
  id: string;
  encompassLoanId: string;
  loanNumber: string;
  borrowerPairId: string | null;
  planCode: PlanCodeInfo | null;
  currentDisclosureRecipientEmails: string[];
  borrower: {
    name: string;
    email: string | null;
  };
  coborrower: {
    name: string;
    email: string | null;
  } | null;
  loanOfficer: {
    name: string;
    email: string | null;
  };
}

export interface AuditMessage {
  severity: 'Error' | 'Warning' | 'Info';
  description: string;
  fields?: string[];
}

export interface AuditFormResult {
  form: {
    name: string;
    type: 'Standard' | 'Custom';
  };
  printable: boolean;
}

export interface PlanCodeConflictResult {
  planCodeID: string;
  planCodeDescription: string;
  noOfConflict: number;
  conflictFields: Array<{
    description: string;
    encompassFieldId: string;
    encompassValue: string;
    planValue: string;
  }>;
}

export interface AuditResult {
  success: boolean;
  auditId: string;
  status: 'RUNNING' | 'SUCCEEDED' | 'WARNING' | 'FAILED';
  messages: AuditMessage[];
  forms?: AuditFormResult[];
  planCodeConflict?: PlanCodeConflictResult | null;
  hasBlockingErrors?: boolean;
  complianceReportUrl?: string | null;
  cachedAt?: string;
}

export interface CachedAuditResult {
  cached: boolean;
  auditId?: string;
  auditResult?: AuditResult;
  cachedAt?: string;
}

export interface AuditHistoryEntry {
  id: string;
  organization_id: string;
  encompass_loan_id: string;
  audit_id: string;
  package_type: string;
  status: string;
  has_blocking_errors: boolean;
  message_count: number;
  messages: AuditMessage[];
  forms: AuditFormResult[];
  plan_code_id: string | null;
  compliance_report_url: string | null;
  created_at: string;
  created_by: string | null;
}

export interface DisclosureDocument {
  id: string;
  name: string;
  type?: string;
  signatureType?: 'eSignable' | 'Informational' | 'WetSignature' | 'Wet Sign Only';
  /** Document difference status from Encompass - Removed docs have no ID and can't be sent */
  difference?: 'NoDiff' | 'Diff' | 'Removed';
  source?: 'Folder' | 'Forms';
  /** Form ID/code used by Encompass (e.g., "URLA", "GTRIDLEAIPJ_S") */
  formId?: string;
  /** Form name from Encompass forms library */
  formName?: string;
  /** Document set category (e.g., "Initial Disclosure") */
  documentSet?: string;
}

export interface DisclosureParty {
  id: string;
  fullName: string;
  type: 'Borrower' | 'CoBorrower' | 'NonBorrowingOwner';
  contact?: {
    email?: string;
    phone?: string;
  };
}

export interface DocumentOrder {
  success: boolean;
  orderId: string;
  status: string;
  documents: DisclosureDocument[];
  parties: DisclosureParty[];
  borrowerPairId?: string;
}

export interface EFolderDocument {
  id: string;
  title: string;
  dateAdded?: string;
  dateRequested?: string;
  status?: string;
}

export interface SendDisclosureResult {
  success: boolean;
  auditId?: string;
  orderId?: string;
  auditWarnings?: string[];
  code?: string;
  staleEmails?: string[];
  message?: string;
  error?: string;
}

// ============================================
// Service
// ============================================

class DisclosureSendService {
  private baseUrl: string;

  constructor() {
    // Use the Supabase Edge Function URL
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    this.baseUrl = `${supabaseUrl}/functions/v1/send-disclosures`;
  }

  /**
   * Get authenticated headers for Edge Function calls
   */
  private async getHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || ''}`
    };
  }

  /**
   * Get loan information including borrower pair ID
   */
  async getLoanInfo(encompassLoanId: string): Promise<DisclosureLoanInfo | null> {
    try {
      log.info('Fetching loan info', { encompassLoanId });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/loan/${encompassLoanId}`, {
        method: 'GET',
        headers
      });

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to fetch loan info', { encompassLoanId, error: data.error });
        return null;
      }

      return {
        ...data.loan,
        currentDisclosureRecipientEmails: data.loan?.currentDisclosureRecipientEmails || []
      };
    } catch (error) {
      log.error('Error fetching loan info', { encompassLoanId }, error as Error);
      return null;
    }
  }

  /**
   * Create and poll audit for a loan
   */
  async createAudit(
    loanId: string,
    borrowerPairId: string,
    packageTypes: string[] = ['AtApp'],
    options?: {
      organizationId?: string;
      userId?: string;
      planCodeId?: string | null;
    }
  ): Promise<AuditResult> {
    try {
      log.info('Creating disclosure audit', { loanId, borrowerPairId });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/audit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          loanId,
          borrowerPairId,
          packageTypes,
          organizationId: options?.organizationId,
          userId: options?.userId,
          planCodeId: options?.planCodeId
        })
      });

      const data = await response.json();

      log.info('Audit result', { loanId, status: data.status, auditId: data.auditId });
      return data;
    } catch (error) {
      log.error('Error creating audit', { loanId }, error as Error);
      return {
        success: false,
        auditId: '',
        status: 'FAILED',
        messages: [{ severity: 'Error', description: (error as Error).message }]
      };
    }
  }

  /**
   * Check for a valid cached audit result
   */
  async getCachedAudit(
    loanId: string,
    packageType: string,
    planCodeId?: string | null
  ): Promise<CachedAuditResult> {
    try {
      log.info('Checking cached audit', { loanId, packageType, planCodeId });

      const headers = await this.getHeaders();
      const params = new URLSearchParams({ packageType });
      if (planCodeId) {
        params.append('planCodeId', planCodeId);
      }

      const response = await fetch(
        `${this.baseUrl}/cached-audit/${loanId}?${params}`,
        { method: 'GET', headers }
      );

      const data = await response.json();

      if (data.cached) {
        log.info('Found cached audit', { loanId, auditId: data.auditId, cachedAt: data.cachedAt });
      } else {
        log.info('No valid cached audit', { loanId, planCodeId });
      }

      return data;
    } catch (error) {
      log.error('Error checking cached audit', { loanId }, error as Error);
      return { cached: false };
    }
  }

  /**
   * Get audit history for a loan
   */
  async getAuditHistory(loanId: string, limit = 10): Promise<AuditHistoryEntry[]> {
    try {
      log.info('Fetching audit history', { loanId, limit });

      const headers = await this.getHeaders();
      const response = await fetch(
        `${this.baseUrl}/audit-history/${loanId}?limit=${limit}`,
        { method: 'GET', headers }
      );

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to fetch audit history', { loanId, error: data.error });
        return [];
      }

      log.info('Fetched audit history', { loanId, count: data.history?.length || 0 });
      return data.history || [];
    } catch (error) {
      log.error('Error fetching audit history', { loanId }, error as Error);
      return [];
    }
  }

  /**
   * Generate document set from audit
   *
   * @param auditId The audit ID from createAudit
   * @param loanId Optional Encompass loan ID - returns recommended forms based on state
   * @param borrowerPairId Borrower pair ID for form operations
   */
  async generateDocSet(
    auditId: string,
    loanId?: string,
    borrowerPairId?: string
  ): Promise<{ order: DocumentOrder | null; error?: string }> {
    try {
      log.info('Generating document set', { auditId, loanId, borrowerPairId });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ auditId, printMode: 'LoanData', loanId, borrowerPairId })
      });

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to generate document set', { auditId, error: data.error });
        return { order: null, error: data.error || 'Failed to generate document set' };
      }

      log.info('Generated document set', {
        auditId,
        orderId: data.orderId,
        docCount: data.documents?.length
      });

      return { order: data };
    } catch (error) {
      log.error('Error generating document set', { auditId }, error as Error);
      return { order: null, error: (error as Error).message };
    }
  }

  /**
   * Get order status
   */
  async getOrderStatus(orderId: string): Promise<DocumentOrder | null> {
    try {
      log.info('Getting order status', { orderId });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/order/${orderId}`, {
        method: 'GET',
        headers
      });

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to get order status', { orderId, error: data.error });
        return null;
      }

      return data;
    } catch (error) {
      log.error('Error getting order status', { orderId }, error as Error);
      return null;
    }
  }

  /**
   * Add documents to an existing order
   */
  async addDocuments(
    orderId: string,
    documents: Array<{ id: string; source: 'Folder' | 'Forms'; name?: string }>
  ): Promise<boolean> {
    try {
      log.info('Adding documents to order', { orderId, count: documents.length });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/add-documents`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ orderId, documents })
      });

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to add documents', { orderId, error: data.error });
        return false;
      }

      return true;
    } catch (error) {
      log.error('Error adding documents', { orderId }, error as Error);
      return false;
    }
  }

  /**
   * Send disclosure package
   */
  async sendPackage(
    loanId: string,
    orderId: string,
    documentIds: string[],
    from: { fullName: string; emailAddress: string },
    to: { id: string; fullName: string; emailAddress: string }
  ): Promise<{ success: boolean; error?: string; code?: string; staleEmails?: string[]; message?: string }> {
    try {
      log.info('Sending disclosure package', { loanId, orderId, docCount: documentIds.length });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/send`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ loanId, orderId, documentIds, from, to })
      });

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to send package', { orderId, error: data.error });
        return {
          success: false,
          error: data.error || data.message,
          code: data.code,
          staleEmails: data.staleEmails,
          message: data.message
        };
      }

      log.info('Successfully sent disclosure package', { orderId });
      return { success: true };
    } catch (error) {
      log.error('Error sending package', { orderId }, error as Error);
      return { success: false, error: (error as Error).message };
    }
  }

  /**
   * Full workflow: audit -> generate -> send
   */
  async sendInitialDisclosures(
    loanId: string,
    borrowerPairId: string,
    from: { fullName: string; emailAddress: string },
    to: { id: string; fullName: string; emailAddress: string },
    additionalDocuments?: Array<{ id: string; source: 'Folder' | 'Forms' }>
  ): Promise<SendDisclosureResult> {
    try {
      log.info('Starting full disclosure workflow', { loanId });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/send-full`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ loanId, borrowerPairId, from, to, additionalDocuments })
      });

      const data = await response.json();

      if (data.success) {
        log.info('Successfully sent disclosures', { loanId, orderId: data.orderId });
      } else {
        log.error('Failed to send disclosures', { loanId, error: data.error });
      }

      return data;
    } catch (error) {
      log.error('Error in full workflow', { loanId }, error as Error);
      return { success: false, error: (error as Error).message };
    }
  }

  /**
   * Get eFolder documents for a loan (available to add to package)
   */
  async getEFolderDocuments(loanId: string): Promise<EFolderDocument[]> {
    try {
      log.info('Fetching eFolder documents', { loanId });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/efolder/${loanId}`, {
        method: 'GET',
        headers
      });

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to fetch eFolder documents', { loanId, error: data.error });
        return [];
      }

      return data.documents || [];
    } catch (error) {
      log.error('Error fetching eFolder documents', { loanId }, error as Error);
      return [];
    }
  }

  /**
   * Get available plan codes
   */
  async getPlanCodes(type: 'opening' | 'closing' = 'opening'): Promise<PlanCode[]> {
    try {
      log.info('Fetching plan codes', { type });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/plan-codes?type=${type}`, {
        method: 'GET',
        headers
      });

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to fetch plan codes', { error: data.error });
        return [];
      }

      return data.planCodes || [];
    } catch (error) {
      log.error('Error fetching plan codes', {}, error as Error);
      return [];
    }
  }

  /**
   * Apply a plan code to a loan
   */
  async applyPlanCode(
    loanId: string,
    planCodeId: string,
    orderType: 'opening' | 'closing' = 'opening',
    importMode: 'all' | 'planCodeOnly' = 'all'
  ): Promise<ApplyPlanCodeResult> {
    try {
      log.info('Applying plan code', { loanId, planCodeId, orderType });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/apply-plan-code`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ loanId, planCodeId, orderType, importMode })
      });

      const data = await response.json();

      if (!data.success) {
        log.error('Failed to apply plan code', { loanId, planCodeId, error: data.error });
        return {
          success: false,
          status: 'CONFLICT',
          conflicts: data.conflicts || [],
          error: data.error || 'Failed to apply plan code'
        };
      }

      log.info('Applied plan code', { loanId, planCodeId, status: data.status });
      return {
        success: true,
        status: data.status,
        conflicts: data.conflicts || []
      };
    } catch (error) {
      log.error('Error applying plan code', { loanId, planCodeId }, error as Error);
      return {
        success: false,
        status: 'CONFLICT',
        conflicts: []
      };
    }
  }

  /**
   * Generate and download preview PDF from Opening Order documents
   * Fetches document PDFs from the order and merges them into a single PDF
   *
   * @param orderId The document order ID
   * @param documentIds Array of document IDs to include in the preview
   * @param loanId The Encompass loan ID (for ownership validation)
   * @returns Object with blob URL for the merged PDF, and any partial failures
   */
  async getPreviewPdf(
    orderId: string,
    documentIds: string[],
    loanId: string
  ): Promise<{
    success: boolean;
    blobUrl?: string;
    failures?: Array<{ documentId: string; error: string }>;
    error?: string;
  }> {
    try {
      log.info('Generating preview PDF from Opening Order', {
        orderId,
        loanId,
        documentCount: documentIds.length
      });

      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/preview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ orderId, documentIds, loanId })
      });

      // Check if we got a PDF or an error JSON response
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/pdf')) {
        // Success - got the merged PDF
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        // Check for partial failures in response header
        const failuresHeader = response.headers.get('X-Preview-Failures');
        let failures: Array<{ documentId: string; error: string }> | undefined;
        if (failuresHeader) {
          try {
            failures = JSON.parse(failuresHeader);
            log.warn('Preview PDF had partial failures', {
              orderId,
              successCount: documentIds.length - (failures?.length || 0),
              failureCount: failures?.length || 0
            });
          } catch {
            log.warn('Could not parse X-Preview-Failures header', { failuresHeader });
          }
        }

        log.info('Successfully generated preview PDF', {
          orderId,
          size: blob.size,
          failureCount: failures?.length || 0
        });
        return { success: true, blobUrl, failures };
      } else {
        // Error response - parse as JSON
        const data = await response.json();
        log.error('Failed to generate preview PDF', { orderId, error: data.error });
        return { success: false, error: data.error || 'Failed to generate preview' };
      }
    } catch (error) {
      log.error('Error generating preview PDF', { orderId }, error as Error);
      return { success: false, error: (error as Error).message };
    }
  }

  /**
   * Get cached document order and preview data for a loan
   * Called to check if we can skip document generation
   */
  async getCachedDocumentOrder(
    loanId: string,
    planCodeId?: string | null
  ): Promise<{
    cached: boolean;
    orderId?: string;
    documents?: DisclosureDocument[];
    parties?: DisclosureParty[];
    previewUrl?: string;
    previewHash?: string;
    generatedAt?: string;
  }> {
    try {
      log.info('Checking cached document order', { loanId });

      // Query the database directly for cached data
      const { data, error } = await supabase
        .from('encompass_loans')
        .select(`
          disclosure_order_id,
          disclosure_order_status,
          disclosure_order_documents,
          disclosure_order_parties,
          disclosure_order_generated_at,
          disclosure_preview_url,
          disclosure_preview_hash,
          disclosure_preview_generated_at,
          disclosure_audit_fields_hash,
          disclosure_audit_plan_code_id,
          disclosure_fields_hash
        `)
        .eq('encompass_loan_id', loanId)
        .single();

      if (error || !data) {
        log.info('No cached document order found', { loanId });
        return { cached: false };
      }

      // Check if cache is valid:
      // 1. Order exists and succeeded
      // 2. Field hashes match (loan data hasn't changed since audit)
      // 3. Plan code matches (if specified)
      // 4. Parties exist (Send requires at least one borrower party)
      const planCodeMatches = !planCodeId || data.disclosure_audit_plan_code_id === planCodeId;
      const hasParties = Array.isArray(data.disclosure_order_parties) && data.disclosure_order_parties.length > 0;

      const hasValidOrder =
        data.disclosure_order_id &&
        data.disclosure_order_status === 'SUCCEEDED' &&
        data.disclosure_order_documents &&
        data.disclosure_audit_fields_hash &&
        data.disclosure_audit_fields_hash === data.disclosure_fields_hash &&
        planCodeMatches &&
        hasParties;

      if (!hasValidOrder) {
        log.info('Cached document order invalid or stale', {
          loanId,
          hasOrderId: !!data.disclosure_order_id,
          status: data.disclosure_order_status,
          hashMatch: data.disclosure_audit_fields_hash === data.disclosure_fields_hash,
          planCodeMatch: planCodeMatches,
          hasParties
        });
        return { cached: false };
      }

      log.info('Found valid cached document order', {
        loanId,
        orderId: data.disclosure_order_id,
        docCount: data.disclosure_order_documents?.length,
        hasPreview: !!data.disclosure_preview_url
      });

      return {
        cached: true,
        orderId: data.disclosure_order_id,
        documents: data.disclosure_order_documents as DisclosureDocument[],
        parties: data.disclosure_order_parties as DisclosureParty[],
        previewUrl: data.disclosure_preview_url,
        previewHash: data.disclosure_preview_hash,
        generatedAt: data.disclosure_order_generated_at
      };
    } catch (error) {
      log.error('Error checking cached document order', { loanId }, error as Error);
      return { cached: false };
    }
  }

  /**
   * Fetch cached preview PDF from storage URL
   */
  async getCachedPreviewPdf(
    previewUrl: string,
    currentSelectionHash: string,
    cachedHash: string | null
  ): Promise<{
    success: boolean;
    blobUrl?: string;
    error?: string;
  }> {
    try {
      // Check if selection hash matches cached hash
      if (!cachedHash || currentSelectionHash !== cachedHash) {
        log.info('Preview hash mismatch, cache invalid', {
          currentHash: currentSelectionHash.substring(0, 20),
          cachedHash: cachedHash?.substring(0, 20)
        });
        return { success: false, error: 'Selection changed' };
      }

      log.info('Fetching cached preview PDF', { previewUrl });

      const response = await fetch(previewUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      log.info('Successfully fetched cached preview PDF', { size: blob.size });
      return { success: true, blobUrl };
    } catch (error) {
      log.error('Error fetching cached preview PDF', { previewUrl }, error as Error);
      return { success: false, error: (error as Error).message };
    }
  }
}

export const disclosureSendService = new DisclosureSendService();
